import styles from './FaqButtonAccordion.module.scss';

import cx from 'classnames';
import uniqueId from 'lodash/uniqueId';
import React, { useEffect, useRef } from 'react';

import { Accordion } from '../Accordion';
import type { IAccordionItem, AccordionItemId, OpenSections } from '../models';
import { FaqButtonAccordionIcon } from './FaqButtonAccordionIcon';

interface IFaqButtonAccordionProps {
  items: IAccordionItem[];
  className?: string;
  variant?: boolean;
  isDisplayViewMore?: boolean;
  isShowAll: boolean;
  limitEntryCount: number;
}

const getAccordionRowsJSX = (
  items: IAccordionItem[],
  onToggled: (id: AccordionItemId) => void,
  openSections: OpenSections,
  variant: boolean,
  isDisplayViewMore = false,
  isShowAll: boolean,
  limitEntryCount: number,
) => {
  const isDisplayItems = isDisplayViewMore && !isShowAll;

  return items.map((item, index) => (
    <div className={cx({ [styles.variantArrowIcon]: variant })} key={item.id}>
      <Accordion.Header
        itemId={item.id}
        defaultOpen={item.defaultOpen}
        onToggled={onToggled}
        openSections={openSections}
        className={cx(item.headerClassName, styles.header, {
          [styles.hideItems]: isDisplayItems && index > limitEntryCount - 1,
        })}
        tabIndex={isDisplayItems && index > limitEntryCount - 1 ? -1 : 0}
      >
        <FaqButtonAccordionIcon open={openSections[item.id]} variant={variant} />
        {item.title}
      </Accordion.Header>
      <Accordion.Body itemId={item.id} openSections={openSections} className={styles.body}>
        <div className={styles.bodyContainer}>{item.body}</div>
      </Accordion.Body>
    </div>
  ));
};

const FaqButtonAccordion: React.FC<IFaqButtonAccordionProps> = ({
  items,
  className,
  variant = false,
  isDisplayViewMore,
  isShowAll,
  limitEntryCount,
}) => {
  const { current: id } = useRef(uniqueId('faq'));

  useEffect(() => {
    if (!isShowAll) return;

    const accordionElements = document.querySelectorAll<HTMLElement>(`.${id} .${styles.header}`);
    const firstElementOnReveal = accordionElements[limitEntryCount];
    firstElementOnReveal?.focus();
  }, [id, isShowAll, limitEntryCount]);

  return (
    <Accordion className={cx(id, styles.faqAccordion, className, { [styles.variantArrowMain]: variant })}>
      {(onToggled, openSections) =>
        getAccordionRowsJSX(
          items,
          onToggled,
          openSections,
          variant,
          isDisplayViewMore,
          isShowAll,
          limitEntryCount,
        )
      }
    </Accordion>
  );
};

export { FaqButtonAccordion };

FaqButtonAccordion.displayName = 'FaqButtonAccordion';
